<script setup lang="ts">
import { computed } from 'vue'
import Avatar from '@/components/xpress/Avatar.vue'
import SpeechBubble from './SpeechBubble.vue'
import Star from './Star.vue'
import { useCurrencyFormat } from '@/utils'
import { useAppStore } from '@/stores/AppStore'

interface Props {
  userPoints?: number
  expireDate?: string
}
interface Emits {
  (e: 'onClickProfile'): void
  (e: 'onClickPoint'): void
}
const props = withDefaults(defineProps<Props>(), {
  userPoints: 0,
  expireDate: '',
})
const emit = defineEmits<Emits>()

const appStore = useAppStore()

// Dynamic text size based on points length
const pointsTextSize = computed(() => {
  const length = String(props.userPoints).length
  if (length <= 2) return 'tw-text-5xl'
  if (length <= 4) return 'tw-text-4xl'
  if (length <= 6) return 'tw-text-3xl'
  return 'tw-text-2xl'
})
const ptsTextSize = computed(() => {
  const length = String(props.userPoints).length
  if (length <= 2) return 'tw-text-xl'
  if (length <= 4) return 'tw-text-lg'
  if (length <= 6) return 'tw-text-base'
  return 'tw-text-sm'
})

const goToProfileSetting = () => {
  emit('onClickProfile')
}

// const goToPointsDetail = () => {
//   emit('onClickPoint')
// }
</script>

<template>
  <!-- Profile Card -->
  <div class="tw-rounded-3xl tw-cursor-pointer tw-relative" style="min-height: 200px">
    <!-- Welcome Speech Bubble -->
    <div class="tw-absolute tw-top-5 tw-left-3 tw-z-10">
      <SpeechBubble message="Welcome!" />
    </div>

    <!-- Decorative Star top left -->
    <div class="tw-absolute tw-top-4 tw-left-[calc(50%-85px)] tw-z-30">
      <Star :size="30" color="#FFF378" />
      <!-- <span class="tw-text-yellow-300 tw-text-[30px]">✦</span> -->
    </div>
    <!-- Decorative Star top right -->
    <div class="tw-absolute -tw-top-3 tw-right-[calc(50%-80px)] tw-z-30">
      <Star :size="30" color="#FFF378" />
    </div>
    <!-- Decorative Star bottom left -->
    <div class="tw-absolute tw-top-[calc(50%-20px)] tw-left-[calc(50%-70px)] tw-z-30">
      <Star :size="15" color="#FFF378" />
    </div>
    <!-- Decorative Star bottom right -->
    <div class="tw-absolute tw-top-20 tw-right-[calc(50%-65px)] tw-z-30">
      <Star :size="15" color="#FFF378" />
    </div>

    <!-- Avatar with Character - Positioned to overlap white box -->
    <div
      class="tw-flex tw-justify-center tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-top-3 tw-z-20"
      @click="goToProfileSetting"
    >
      <!-- Main Avatar Circle -->
      <Avatar :src="appStore.userProfile.pictureUrl" />
    </div>

    <!-- White Bottom Box - Half height -->
    <div
      class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-items-center tw-justify-end tw-pb-4 tw-px-4"
      style="height: 70%"
    >
      <!-- Points Display Section -->
      <div class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-3">
        <!-- Thai Text: คะแนนสะสมทั้งหมด (Total Points) -->
        <div class="tw-text-center tw-w-[60px]">
          <p class="tw-text-sm tw-font-medium tw-text-gray-700">คะแนนสะสมทั้งหมด</p>
        </div>
        <!-- Points Icon -->
        <div
          class="tw-w-8 tw-h-8 tw-bg-gradient-to-br tw-from-yellow-400 tw-to-yellow-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-md"
        >
          <span class="tw-text-white tw-font-bold tw-text-xl">P</span>
        </div>

        <!-- Points Number -->
        <div class="tw-flex tw-items-baseline tw-gap-1">
          <span
            :class="[pointsTextSize, 'tw-font-bold', 'tw-text-gray-800']"
            style="font-family: system-ui, -apple-system, sans-serif"
            >{{ useCurrencyFormat(userPoints) }}</span
          >
          <span :class="[ptsTextSize, 'tw-font-semibold', 'tw-text-gray-600']">Pts</span>
        </div>

        <!-- Arrow Icon -->
        <!-- <div class="tw-ml-1">
          <div class="tw-w-9 tw-h-9 tw-bg-gray-200 tw-rounded-full tw-flex tw-items-center tw-justify-center">
            <van-icon name="arrow" size="22" color="#666" />
          </div>
        </div> -->
      </div>

      <!-- Expiry Date -->
      <div class="tw-text-center">
        <p class="tw-text-xs tw-text-gray-500">คะแนนจะหมดอายุวันที่ {{ expireDate }}</p>
      </div>
    </div>
  </div>
</template>
